"use client";

/**
 * Per-session cap on `gate_impression` events (P2-1, 2026-08-04 product-signal
 * audit — docs/plans/backlog/product-signal-audit-remediation-2026-08-04.md
 * §12). The audit found the average gated visitor saw 11.9 `gate_impression`
 * fires for a 3.0% CTA click-through — repeat exposure within one session was
 * driving PostHog event/person volume (and the `monetized_suppressed`
 * downstream load it feeds) far past the point of any incremental signal.
 *
 * 3 is chosen, not lower, because a visitor who hits a wall exactly once is
 * indistinguishable from noise, but hitting the SAME session-scoped wall
 * repeatedly is worth keeping a countable signal for (funnel debugging,
 * `gate_ctr` denominators) without re-inflating the exact "18 walls per user"
 * granularity collapse already diagnosed in
 * docs/plans/archive/telemetry-vs-reality-gap-2026-07-23.md.
 */
export const MAX_GATE_IMPRESSIONS_PER_SESSION = 3;

const SESSION_KEY = "ld_gate_impression_count";

/**
 * Increments and returns this session's `gate_impression` count.
 * `sessionStorage` (not `localStorage`) is deliberate — the cap is "per
 * person per session": a new tab/session starts a fresh budget, matching how
 * every other session-scoped guardrail in this codebase behaves (see
 * `src/lib/monetization/use-ad-guardrails.ts`).
 */
function nextSessionCount(): number {
	if (typeof window === "undefined") return 1;
	try {
		const current = Number(window.sessionStorage.getItem(SESSION_KEY) ?? "0");
		const next = (Number.isFinite(current) ? current : 0) + 1;
		window.sessionStorage.setItem(SESSION_KEY, String(next));
		return next;
	} catch {
		// sessionStorage unavailable (private mode / storage disabled) — fail
		// open (never block the gate) rather than throw off the render path.
		return 1;
	}
}

/**
 * Whether the caller may still fire a `gate_impression` event this session.
 * Increments the session counter as a side effect, so call this AT MOST ONCE
 * per would-be impression (mirrors `trackGateImpression`'s own call
 * discipline — each gate component fires it once per mount/decision).
 */
export function isWithinGateImpressionCap(): boolean {
	return nextSessionCount() <= MAX_GATE_IMPRESSIONS_PER_SESSION;
}

/** Test-only: reset the session counter between test cases. */
export function __resetGateImpressionCapForTest(): void {
	if (typeof window === "undefined") return;
	try {
		window.sessionStorage.removeItem(SESSION_KEY);
	} catch {
		/* ignore */
	}
}
